import React, { useEffect, useState, useCallback } from 'react';
import MapEngine from '../components/harvesting/MapEngine';
import { getPollutionMap } from '../services/aggregateService';

// ─── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', maxWidth: 1200, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  sub:     { color: '#6b7280', fontSize: 14, marginBottom: 24 },
  row:     { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
  stat:    {
    background: '#f9fafb', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: '12px 18px', textAlign: 'center', minWidth: 110,
  },
  statNum:  { fontSize: 22, fontWeight: 700, color: '#059669' },
  statLbl:  { fontSize: 12, color: '#6b7280', marginTop: 2 },
  err:      {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  info:     {
    background: '#f0fdf4', border: '1px solid #bbf7d0',
    borderRadius: 6, padding: '10px 14px', color: '#166534', fontSize: 13, marginBottom: 16,
  },
  detail:   {
    marginTop: 16, background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: '14px 18px',
  },
};

// Environmental records are a single category — one data layer
const toDataLayers = (records) =>
  records.length ? [{ dataTypeId: 'environmental', data: records }] : [];

const AggregatePollutionMapPage = () => {
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getPollutionMap();
      setRecords(data.records || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load environmental observation data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const communities = new Set(records.map(r => r.tenantId?.name).filter(Boolean)).size;
  const speciesSet  = new Set(records.map(r => r.species).filter(Boolean)).size;

  return (
    <div style={S.page}>
      <h1 style={S.heading}>Environmental Observations Map</h1>
      <p style={S.sub}>
        Cross-community view of approved environmental observations with geographic coordinates.
      </p>

      <div style={S.row}>
        <div style={S.stat}>
          <div style={S.statNum}>{records.length}</div>
          <div style={S.statLbl}>Observations</div>
        </div>
        <div style={S.stat}>
          <div style={S.statNum}>{communities}</div>
          <div style={S.statLbl}>Communities</div>
        </div>
        <div style={S.stat}>
          <div style={S.statNum}>{speciesSet}</div>
          <div style={S.statLbl}>Reported Species</div>
        </div>
      </div>

      <div style={S.info}>
        🌍 Showing all approved environmental observations across participating communities.
        Click a marker on the map to see record details.
      </div>

      {error && <div style={S.err}>{error}</div>}

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading map data…</p>
      ) : (
        <MapEngine
          dataLayers={toDataLayers(records)}
          height="580px"
          onFeatureClick={setSelected}
        />
      )}

      {selected && (
        <div style={S.detail}>
          <strong>Selected Observation</strong>
          <div style={{ fontSize: 13, marginTop: 8, color: '#374151' }}>
            <div><b>Species / Subject:</b> {selected.species || '—'}</div>
            <div><b>Community:</b> {selected.tenantId?.name || '—'}</div>
            <div><b>Location:</b> {selected.locationDescription || '—'}</div>
            <div><b>Notes:</b> {selected.notes || '—'}</div>
            <div><b>Date:</b> {selected.harvestDate ? new Date(selected.harvestDate).toLocaleDateString() : '—'}</div>
          </div>
          <button
            onClick={() => setSelected(null)}
            style={{ marginTop: 10, fontSize: 12, cursor: 'pointer', background: 'none', border: 'none', color: '#6b7280' }}
          >
            ✕ Close
          </button>
        </div>
      )}
    </div>
  );
};

export default AggregatePollutionMapPage;
